import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { decodeFeedPostBody } from "@heylife/i18n";
import { api, fmtDate, hasLocalizedText, normalizeLocalizedRequired, scopeQuery, useScope, type Localized } from "../api";
import { useT } from "../i18n";
import { Field, LocalizedField, Msg, useFlash } from "../ui";

type Props = {
  onClose: () => void;
  onCreated: () => void;
};

export function CustomAnnouncementDialog({ onClose, onCreated }: Props) {
  const { t } = useT();
  const scope = useScope();
  const sq = scopeQuery(scope, "church");
  const [flash, run, busy] = useFlash();
  const [title, setTitle] = useState<Localized>({ de: "", en: "" });
  const [body, setBody] = useState<Localized>({ de: "", en: "" });
  const [push, setPush] = useState(true);
  const [email, setEmail] = useState(false);
  const [publishToFeed, setPublishToFeed] = useState(true);
  const aq = useQuery({
    queryKey: ["admin", "audiences", scope.churchId, "hey_church"],
    queryFn: () => api<{ counts: Record<string, number> }>(`/admin/notifications/audiences${sq}`),
    enabled: Boolean(scope.churchId),
  });
  const recipientCount = aq.data?.counts.hey_church ?? 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const channels = [...(push ? (["push"] as const) : []), ...(email ? (["email"] as const) : [])];
    const normTitle = normalizeLocalizedRequired(title);
    const normBody = normalizeLocalizedRequired(body);
    if (channels.length === 0 || !normTitle || !normBody) return;
    const payload = {
      title: normTitle,
      body: normBody,
      channels,
      publishToFeed,
      audience: "hey_church" as const,
    };
    const ok = await run(
      () => api(`/admin/notifications/custom-announcements${sq}`, { method: "POST", json: payload }),
      t("admin.notifications.automatic.othersCreated"),
    );
    if (ok) {
      onCreated();
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={(e) => void submit(e)}>
        <h2>{t("admin.notifications.automatic.othersCreateTitle")}</h2>
        <p className="muted">{t("admin.notifications.automatic.othersCreateHint")}</p>
        <LocalizedField label={t("admin.notifications.titleLabel")} value={title} onChange={setTitle} required />
        <LocalizedField label={t("admin.notifications.textLabel")} value={body} onChange={setBody} multiline required />
        <Field label={t("admin.notifications.medium")}>
          <div className="chips">
            <label className={`chip ${push ? "on" : ""}`}>
              <input type="checkbox" checked={push} onChange={(e) => setPush(e.target.checked)} hidden />
              {t("admin.notifications.via.push")}
            </label>
            <label className={`chip ${email ? "on" : ""}`}>
              <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} hidden />
              {t("admin.notifications.via.email")}
            </label>
          </div>
        </Field>
        <label className="toggle">
          <input type="checkbox" checked={publishToFeed} onChange={(e) => setPublishToFeed(e.target.checked)} />
          <span>{t("admin.notifications.automatic.othersPublishFeed")}</span>
        </label>
        <p className="muted small">{t("admin.notifications.automatic.othersAudience", { n: recipientCount })}</p>
        <div className="actions">
          <button type="submit" className="btn" disabled={busy || !hasLocalizedText(title) || !hasLocalizedText(body) || (!push && !email)}>
            {t("admin.notifications.automatic.othersSend")}
          </button>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t("admin.common.cancel")}
          </button>
          <Msg msg={flash} />
        </div>
      </form>
    </div>
  );
}

export type CustomAnnouncement = { id: string; body: string; createdAt: string; authorAlias: string | null };

export function CustomAnnouncementCard({ item, locale }: { item: CustomAnnouncement; locale: string }) {
  const { t } = useT();
  const displayBody = decodeFeedPostBody(item.body, locale === "en" ? "en" : "de");
  const parts = displayBody.split("\n\n");
  const headline = parts[0] ?? displayBody;
  const rest = parts.slice(1);
  const preview = rest.join("\n\n").trim() || headline;
  const title = rest.length > 0 ? headline : headline.slice(0, 80);
  return (
    <article className="notification-custom-card">
      <div className="notification-custom-card-head">
        <strong>{title}</strong>
        <span className="pill small">{t("admin.notifications.custom")}</span>
      </div>
      <p className="muted small">{preview}</p>
      <p className="muted small">{t("admin.notifications.automatic.othersFeedPublished")}</p>
      <p className="muted small">{fmtDate(item.createdAt, locale, { dateStyle: "medium", timeStyle: "short" })}</p>
    </article>
  );
}
